import { useCallback } from 'react';
import { minBy } from 'lodash';
import BigNumber from 'bignumber.js';
import { PositionView } from '#/positions/entities/positions';
import { AuctionType } from '#/unified-rfqs/entities/shared';
import { getProduct } from '#/products/domain';
import { RATIO_PRECISION } from '#/unified-rfqs/ui/ratio-precision';
import { useRfqBuilderModalMethods } from '#/unified-rfqs/ui/rfq-builder/modal-context';
import {
  hasSameAccount,
  hasSameProduct,
} from '#/positions/contexts/position-groups';
import { DrfqPayload, GrfqPayload, useMarketPayloadCreator } from './shared';

function getLegRatioAndSide(
  position: PositionView,
  minQty: BigNumber,
  type: AuctionType,
  isMultiple: boolean,
) {
  if (!isMultiple) {
    // For a single leg the ratio is one and the side is fixed at BUY
    return { side: 'BUY', ratio: '1' } as const;
  }

  const product = getProduct(position.productCode);
  const absoluteQty = position.quantity.value.absoluteValue();

  return {
    ratio: absoluteQty
      .dividedBy(minQty)
      .decimalPlaces(RATIO_PRECISION[type][product.kind])
      .absoluteValue()
      .toString(),
    side: position.quantity.value.isPositive() ? 'BUY' : 'SELL',
  } as const;
}

function makeDrfqPayload(
  positions: readonly PositionView[],
  type: AuctionType,
): DrfqPayload {
  const minQtyPosition = minBy(positions, (p) =>
    p.quantity.value.absoluteValue().toNumber(),
  );
  if (minQtyPosition == null)
    throw new TypeError('At least one position is expected.');
  const { description, venue } = minQtyPosition;
  const minQty = minQtyPosition.quantity;

  return {
    counterparties: [],
    legs: positions.map((p) => ({
      quantity: p.quantity.value.absoluteValue().toString(),
      instrument_id: 0,
      instrument_name: p.venue_instrument_name,
      isHedge: false,
      product_code: p.productCode,
      ...getLegRatioAndSide(p, minQty.value, type, positions.length > 1),
    })),
    role: 'TAKER',
    // Should use configured default account as per PTP-18837
    account_name: null,
    product_codes: positions.map((p) => p.productCode),
    description: positions.length === 1 ? description : 'Cstm',
    venue,
    type: 'drfq',
  };
}

function makeGrfqPayload(
  positions: readonly PositionView[],
  type: AuctionType,
): GrfqPayload {
  const minQtyPosition = minBy(positions, (p) =>
    p.quantity.value.absoluteValue().toNumber(),
  );
  if (minQtyPosition == null)
    throw new TypeError('At least one position is expected.');
  const { description, venue } = minQtyPosition;
  const minQty = minQtyPosition.quantity;

  return {
    legs: positions.map((p) => ({
      instrument: p.venue_instrument_name,
      isHedge: false,
      product_code: p.productCode,
      ...getLegRatioAndSide(p, minQty.value, type, positions.length > 1),
    })),
    type: 'grfq',
    venue,
    product_codes: positions.map((p) => p.productCode),
    description: positions.length === 1 ? description : 'Cstm',
  };
}

interface Props {
  readonly positions: readonly PositionView[];
  readonly onError: (message: string) => void;
}

export default function useCreateMarketFromPositions({
  onError,
  positions,
}: Props) {
  const { open: openMarketBuilder } = useRfqBuilderModalMethods();
  const makeMarketBuilderState = useMarketPayloadCreator();
  const createMarket = useCallback(
    (auctionType: AuctionType) => {
      const makePayload =
        auctionType === 'RFQ' ? makeDrfqPayload : makeGrfqPayload;

      if (positions.length === 0) {
        onError('No positions selected');
        return;
      }

      if (!hasSameProduct(positions)) {
        onError(
          'RFQs and OBs can be created only per product (i.e. BTC Option - DBT)',
        );
        return;
      }

      if (!hasSameAccount(positions)) {
        onError('Positions in different accounts selected');
        return;
      }

      const payload = makePayload(positions, auctionType);
      const state = makeMarketBuilderState(payload, auctionType);
      openMarketBuilder(state, onError);
    },
    [makeMarketBuilderState, onError, openMarketBuilder, positions],
  );

  return createMarket;
}
